"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.savePermissions = exports.handleUpdatePermissions = exports.fetchProjectFolderPermissions = exports.updateTree = void 0;
const t = require("io-ts");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const sok_types_1 = require("@caddigital/sok-types");
const ActionTypes = require("../../actionTypes/project/permissions");
const Actions = require("../../actions/project/permissions");
const _crudActionTypes = require("../../modules/files/crud/actionTypes");
const _treeActions = require("../../modules/files/tree/actions");
const _usergroupListActionTypes = require("../../modules/usergroup/list/actionTypes");
const acl_1 = require("../../utils/acl");
const permissions_1 = require("../../utils/permissions");
const errorHandling_1 = require("../../utils/errorHandling");
const crudActionTypes = _crudActionTypes.default('permissions');
const treeActions = _treeActions.default('permissions');
const usergroupListActionTypes = _usergroupListActionTypes.default('project/members');
function updateTree(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === crudActionTypes.DELETE_SUCCESS ||
        action.type === crudActionTypes.SAVE_NEW_FOLDER_SUCCESS ||
        action.type === crudActionTypes.SAVE_PROPERTIES_SUCCESS), (0, operators_1.mergeMap)((action) => {
        const state = store.value;
        const { folderTree } = state.project.permissions;
        if (action.type === crudActionTypes.SAVE_NEW_FOLDER_SUCCESS) {
            const selected = folderTree.target;
            if (selected) {
                return (0, rxjs_1.of)(treeActions.fetchTree(selected.getValue()));
            }
        }
        const tree = folderTree.data.tree;
        if (!tree) {
            return rxjs_1.EMPTY;
        }
        const fetch = [];
        let files = [];
        if (action.type === crudActionTypes.DELETE_SUCCESS) {
            files = action.files;
        }
        else if (action.type === crudActionTypes.SAVE_PROPERTIES_SUCCESS) {
            files.push(action.file);
        }
        const nodes = tree.getNodes();
        for (const file of files) {
            const node = nodes.find((x) => x.getValue().id === file.id);
            if (node) {
                const rawNode = node.getValue();
                if (rawNode.id === rawNode.parent) {
                    fetch.push(treeActions.fetchTree(tree.getRoot().getValue()));
                }
                else {
                    const parent = node.getParentNode();
                    if (parent) {
                        fetch.push(treeActions.fetchTree(parent.getValue()));
                    }
                }
            }
        }
        return (0, rxjs_1.of)(...fetch);
    }));
}
exports.updateTree = updateTree;
function fetchProjectFolderPermissions(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_PROJECT_FOLDER_PERMISSIONS_REQUEST), (0, operators_1.mergeMap)(({ payload: { projectId, folderIdentifier } }) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/project/${projectId}/folders/${folderIdentifier}/permissions`
    }).pipe((0, operators_1.mergeMap)((res) => {
        const validate = t
            .array(sok_types_1.Project.iFolderPermission)
            .decode(res.response);
        const act = validate.fold((e) => Actions.fetchProjectFolderPermissionsFailure((0, errorHandling_1.parseValidationError)(e)), (permissions) => {
            return Actions.fetchProjectFolderPermissionsSuccess(permissions);
        });
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((e) => (0, rxjs_1.of)(Actions.fetchProjectFolderPermissionsFailure((0, errorHandling_1.parseAjaxErrorResponse)(e)))))));
}
exports.fetchProjectFolderPermissions = fetchProjectFolderPermissions;
const permissionGroups = [
    'NormalUserGroup',
    'CategoryUserGroup',
    'CustomerUserGroup',
    'CustomerCategoryUserGroup'
];
/**
 * handleUpdatePermissions epic populate all permissions
 */
function handleUpdatePermissions(action$, store) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.UPDATE_PERMISSION ||
        action.type === ActionTypes.FETCH_PROJECT_FOLDER_PERMISSIONS_SUCCESS ||
        action.type === ActionTypes.PROJECT_FOLDER_PERMISSIONS_RESET ||
        action.type === ActionTypes.TOGGLE_NEW_FOLDER_PERMISSION ||
        action.type === usergroupListActionTypes.FETCH_SUCCESS ||
        action.type === ActionTypes.FORCE_PROCESS_PERMISSIONS), (0, operators_1.mergeMap)(() => {
        const state = store.value;
        // Ignore if wrong tab
        if (state.project.members.currentTab !== 1) {
            return rxjs_1.EMPTY;
        }
        const currentProject = state.project.currentProject;
        if (currentProject.status !== 'FETCHED') {
            return rxjs_1.EMPTY;
        }
        const folders = state.project.permissions.folderTree.data.tree
            ? state.project.permissions.folderTree.data.tree
                .getNodes()
                .map((f) => (Object.assign(Object.assign({}, f.getValue()), { identifier: f.getValue().uuid, parentIdentifier: f.getValue().parentUuid })))
            : [];
        const permissions = state.project.permissions;
        //#region  Preparing data
        const categories = state.project.members.groups.filter((x) => permissionGroups.includes(x.category.type));
        //#region  Preparing data
        const categoryGroups = categories
            .filter((x) => x.groups.length)
            .map((cat) => cat.category.uuid);
        const normalUserGroups = categories.reduce((res, cat) => {
            return res.concat(cat.groups.map((g) => g.uuid));
        }, []);
        const allGroups = normalUserGroups;
        const nodes = state.project.permissions.folderTree.data.tree
            ? state.project.permissions.folderTree.data.tree.getNodes()
            : [];
        const selected = nodes.find((f) => f.getSelected() === true);
        const path = [];
        let subproject = null;
        let ignoredGroups = {};
        let selectedFolder = undefined;
        if (selected) {
            selectedFolder = selected.getValue();
            function getPath(folder) {
                path.unshift(folder);
                if (((folder.feature || 0x00) & acl_1.SUBPROJECT_PERM) > 0) {
                    const meta = state.files.metadata.list.find((x) => x.identifier === 'subproject-status-meta');
                    if (meta && meta.id != undefined) {
                        const activeOption = (meta.options || []).find((x) => x.name === 'active');
                        if (activeOption) {
                            if (folder.customMeta[meta.id] === activeOption.value)
                                subproject = folder;
                        }
                    }
                }
                if (folder.id === folder.parent) {
                    return;
                }
                const parent = folders.find((x) => x.id === folder.parent);
                if (parent) {
                    getPath(parent);
                }
            }
            getPath(selectedFolder);
            if (subproject) {
                ignoredGroups = state.project.members.groups.reduce((acc, item) => {
                    if (item.category.type !== 'CustomerCategoryUserGroup') {
                        return acc;
                    }
                    acc[item.category.uuid] = item.category;
                    return item.groups.reduce((ac, group) => {
                        if (group.type === 'CustomerUserGroup') {
                            ac[group.uuid] = group;
                        }
                        return ac;
                    }, acc);
                }, {});
            }
        }
        //#endregion
        //#region Initializing
        // Init folder will create basic structure for folder and set given permissions
        const initFolder = (disabledGroupIds = []) => ({
            hasAll: permissions_1.ACL_PERM_NONE,
            hasAllCustomerGroups: permissions_1.ACL_PERM_NONE,
            categories: {},
            groups: {},
            disabledGroupIds
        });
        // Initialize
        const processed = {};
        if (selectedFolder !== undefined) {
            processed[selectedFolder.uuid] = initFolder(Object.values(ignoredGroups).map((x) => x.id));
        }
        //#endregion
        // Resolve group permissions
        function resolveGroupPermission(folder, group) {
            const reversed = permissions.changes.reduceRight((r, i) => {
                r.push(i);
                return r;
            }, []);
            // Check if we have exact match
            const exact = reversed.find((per) => per.folder === folder && per.group === group);
            if (exact) {
                return exact.permission;
            }
            // Find parent permission
            function findParentPermission(parent) {
                if (subproject &&
                    subproject.parentUuid === parent &&
                    ignoredGroups[group]) {
                    return NaN;
                }
                // Check if parent folder has permission and has include sub folders
                const found = reversed.find((per) => per.folder === parent && per.group === group);
                if (found && found.includeSubFolders) {
                    return found.permission;
                }
                // if parent permission not found or include sub folder is false
                // then we continue
                const next = folders.find((f) => f.identifier === parent);
                if (next && next.parentIdentifier !== next.identifier) {
                    return findParentPermission(next.parentIdentifier);
                }
                // end of tree
                return NaN;
            }
            // Resolve parent folder
            const fol = folders.find((f) => f.identifier === folder);
            if (fol) {
                return findParentPermission(fol.parentIdentifier);
            }
            // folder do not have any parent folders
            return NaN;
        }
        //#region Processsing
        // Process all groups
        allGroups.reduce((res, group) => {
            // initialize folder if not exist
            let fol;
            if (selectedFolder) {
                // Resolve correct permissions
                fol = res[selectedFolder.uuid];
                const groupPermissions = resolveGroupPermission(selectedFolder.uuid, group);
                // Set permissions if found
                if (fol && !isNaN(groupPermissions)) {
                    fol.groups[group] = groupPermissions;
                }
            }
            // Check if parent folder(s) has include subfolders
            return res;
        }, processed);
        permissions.currentPermissions.permissions
            .filter((x) => selectedFolder && x.folder === selectedFolder.uuid)
            .reduce((res, permission) => {
            // initialize folder if not exist
            if (selectedFolder) {
                const current = res[selectedFolder.uuid];
                if (current) {
                    if (current.groups[permission.group] === undefined) {
                        current.groups[permission.group] = permission.permissions;
                    }
                }
            }
            return res;
        }, processed);
        const fol = selectedFolder ? processed[selectedFolder.uuid] : undefined;
        if (fol) {
            // Check if all groups from category is selected
            fol.categories = categories.reduce((res, item) => {
                // total groupd
                const total = item.groups.length;
                if (total === 0) {
                    // If category do not have any groups, lets set denied
                    res[item.category.uuid] = permissions_1.ACL_PERM_NONE;
                }
                else if (total === 1) {
                    // if category has only one group
                    const temp = fol.groups[item.groups[0].uuid];
                    if (temp !== undefined) {
                        res[item.category.uuid] = temp;
                    }
                    else {
                        res[item.category.uuid] = permissions_1.ACL_PERM_NONE;
                    }
                }
                else {
                    // If category have multiple group
                    // we take first and then we compare that to others
                    const temp = fol.groups[item.groups[0].uuid];
                    // if this returns > -1 then groups are not equals
                    const equals = item.groups.findIndex((g, i) => {
                        const temp2 = fol.groups[g.uuid];
                        if (temp !== undefined && temp2 !== undefined) {
                            return temp !== temp2;
                        }
                        return true;
                    });
                    if (equals === -1) {
                        res[item.category.uuid] = temp;
                    }
                    else {
                        res[item.category.uuid] = permissions_1.ACL_PERM_NONE;
                    }
                }
                return res;
            }, {});
            // Check if all categories has been selected
            if (categoryGroups.length > 0) {
                const temp = fol.categories[categoryGroups[0]];
                // Check if all categories are equals
                // if this returns > -1 then categories are not equals
                const equals = categoryGroups.findIndex((uuid, i) => {
                    const temp2 = fol.categories[uuid];
                    if (temp !== undefined && temp2 !== undefined) {
                        return temp !== temp2;
                    }
                    return true;
                });
                // If project does not have any customer groups, we will return true
                // otherwise we compare if first category and customer group is equal
                const customerEqual = true;
                if (fol) {
                    if (equals === -1 && customerEqual) {
                        fol.hasAll = temp;
                    }
                    else {
                        fol.hasAll = permissions_1.ACL_PERM_NONE;
                    }
                }
            }
        }
        //#endregion
        // Dispatching processed data
        return (0, rxjs_1.of)(Actions.setPermissions(processed));
    }));
}
exports.handleUpdatePermissions = handleUpdatePermissions;
function handlePermissions(opts, project, changed) {
    if (changed.length === 0) {
        return (0, rxjs_1.of)([]);
    }
    return opts
        .ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: opts.getToken()
        },
        method: 'PUT',
        body: changed,
        url: `${opts.baseUrl}api/project/${project}/permissions`
    })
        .pipe((0, operators_1.mergeMap)(() => {
        return (0, rxjs_1.of)([]);
    }));
}
function handleNotifications(opts) {
    return (0, rxjs_1.of)([]);
}
function savePermissions(action$, store, opts) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SAVE_PERMISSIONS_REQUEST), (0, operators_1.mergeMap)((_) => {
        const state = store.value;
        const currentProject = state.project.currentProject;
        if (currentProject.status !== 'FETCHED') {
            return rxjs_1.EMPTY;
        }
        const project = currentProject.project.uuid;
        const changes = state.project.permissions.changes;
        // Handle permissions
        return handlePermissions(opts, project, changes).pipe((0, operators_1.map)(() => {
            // Save notifications
            return handleNotifications(opts);
        }), (0, operators_1.mergeMap)(() => {
            // All is done
            return (0, rxjs_1.of)(Actions.savePermissionsSuccess());
        }), (0, operators_1.catchError)((e) => (0, rxjs_1.of)(Actions.savePermissionsFailure((0, errorHandling_1.parseAjaxErrorResponse)(e)))));
    }));
}
exports.savePermissions = savePermissions;
//#endregion
//# sourceMappingURL=permissions.js.map